import React, { useEffect, useState } from 'react';
import { useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import Button from '../../../components/button';
import ToastComponent from '../../../components/toast';
import Heading from '../../../components/typography';
import { COLORS } from '../../../constants/style/color';
import {
  useGetCompanyAccountingPolicyById,
  useGetCompanyAccountingPolicyGroups,
  useWealthlaneIdentityApi,
} from '../../../hooks';
import { CompanyAccountingPolicyService } from '../../../services/wealthlane-identity-services';
import { SettingSystemUserHeader } from '../system-users/systemUserStyled';
import { withDynamicTableContext } from '@wealthlane/table-builder';
import { axiosInstance } from '../../../services/apiInterceptor';
import { FMVContainer } from '../fmv-configuration/fmv.styled';
import FormSelect from '../../../components/dropdown/formSelect';
import { YesNoBooleanOptions } from '../../../constants/common';

const CompanyAccountingPolicy = (): JSX.Element => {
  const queryClient = useQueryClient();

  const { isLoading: isApiLoading, handleRequest } = useWealthlaneIdentityApi();
  const [isSaving, setIsSaving] = useState(false);

  const { data: policyGroups } = useGetCompanyAccountingPolicyGroups(handleRequest);

  const { data: policyById } = useGetCompanyAccountingPolicyById(
    handleRequest,
    policyGroups?.items?.[0]?.id ?? ''
  );

  interface PolicyDataItem {
    id: string;
    systemName: string;
    displayName: string;
    isEnabled: string;
    isActive: string;
  }

  const [policyData, setPolicyData] = useState<PolicyDataItem[]>([]);

  useEffect(() => {
    if (policyById?.items != null && policyById.items.length > 0) {
      setPolicyData(
        policyById.items.map((item) => ({
          id: item.id ?? '',
          systemName: item.systemName ?? '',
          displayName: item.displayName ?? '',
          isEnabled: item.isEnabled != null ? String(item.isEnabled) : '',
          isActive: item.isAssigned != null ? String(item.isAssigned) : '',
        }))
      );
    }
  }, [policyById]);

  const handlePolicyChange = (index: number, value: string) => {
    setPolicyData((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], isActive: value };
      return updated;
    });
  };

  const handleSavePolicy = (policyData: PolicyDataItem[]): void => {
    setIsSaving(true);
    const selections = policyData
      .filter((item) => item.isActive !== '')
      .map((item) => ({
        companyAccountingPolicyId: item.id,
        isAssigned: item.isActive === 'true',
      }));

    if (selections.length === 0) return;

    void handleRequest(
      CompanyAccountingPolicyService.postApiIdentityApiCompanyAccountingPolicyRuleSaveSelections({
        selections,
      })
    )
      .then((res) => {
        if (res != null) {
          void queryClient.invalidateQueries('getCompanyAccountingPolicyList');
          toast.success(
            <ToastComponent label='Success' message='Accounting policy selections saved.' />
          );
          setIsSaving(false);
        }
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  // const handleAssignPolicy = (policyId: string): void => {
  //   void handleRequest(
  //     CompanyAccountingPolicyService.postApiIdentityApiCompanyAccountingPolicyRuleAssign({
  //       companyAccountingPolicyId: policyId,
  //     })
  //   ).then((res) => {
  //     if (res != null) {
  //       void queryClient.invalidateQueries('getCompanyAccountingPolicyList');
  //       toast.success(<ToastComponent label='Success' message='Accounting policy assigned.' />);
  //     }
  //   });
  // };

  // const handleRemovePolicy = (policyId: string): void => {
  //   void handleRequest(
  //     CompanyAccountingPolicyService.deleteApiIdentityApiCompanyAccountingPolicyRuleRemove(policyId)
  //   ).then((res) => {
  //     if (res != null) {
  //       void queryClient.invalidateQueries('getCompanyAccountingPolicyList');
  //       toast.success(<ToastComponent label='Success' message='Accounting policy removed.' />);
  //       closeConfirmModal();
  //     }
  //   });
  // };

  // const handleDeletePolicy = (): void => {
  //   if (selectedPolicyId !== '') {
  //     deletePolicy(selectedPolicyId);
  //   }
  // };

  return (
    <>
      <SettingSystemUserHeader data-page='companyAccountingPolicy' id='pageContainer'>
        <Heading variant='h3' title='Company Accounting Policy' id='pageTitle' />
        <Heading
          variant='body-s'
          title='Configure accounting policies for your company'
          color={`${COLORS.Gray}`}
          id='pageDescription'
        />
      </SettingSystemUserHeader>

      <>
        <FMVContainer id='formContainer'>
          <Heading
            variant='h5'
            title={policyGroups?.items?.[0]?.displayName ?? ''}
            color={COLORS?.Purple}
            id='sectionTitlePolicyName'
          />
          <div className='grid grid-cols-4 mt-4' id='policyNameHeader'>
            <Heading variant='h6' title='Policy Name' id='columnAwardType' className='col-span-2' />
          </div>

          {policyData.map((item, index) => {
            return (
              <div key={item.id} className='grid grid-cols-4 mt-4' id={`row.${index}`}>
                <Heading
                  variant='body-s'
                  title={item.displayName}
                  id={`cell.${index}.policyName`}
                  className='col-span-2'
                />
                <FormSelect
                  type='text'
                  name={item.systemName}
                  id={`${item.systemName}`}
                  containerClassName='formGroup mt-8'
                  onChange={(selectedValue: any) => {
                    handlePolicyChange(index, selectedValue);
                  }}
                  options={YesNoBooleanOptions}
                  isFormik={false}
                  fieldLabelName='fmvType'
                  arrValue={item.isActive !== '' ? item.isActive : undefined}
                />
              </div>
            );
          })}
          <div className='mt-4 flex justify-end'>
            <Button
              variant='primary'
              title='Save'
              icon='check_circle'
              onClick={() => {
                handleSavePolicy(policyData);
              }}
              loading={isSaving}
            />
          </div>
        </FMVContainer>
      </>
    </>
  );
};

export default withDynamicTableContext(CompanyAccountingPolicy, false, axiosInstance);
